import React, { useState, useEffect, useCallback }  from 'react';
import { Switch, Route, Link, BrowserRouter } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import LoginPage from './LoginPage';
import ServicesPage from './ServicesPage';
import ServiceDetailsPage from './ServiceDetailsPage';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import urls from './constants';
import Header from './Header';
import ResourcesPage from './ResourcesPage';
import { useServices } from './hooks/useServices';
import PrayerRequestsPage from './PrayerRequestsPage';
import { useAuthentication } from './hooks/useAuthentication';
import InterpretationPage from './InterpretationPage';
import { useInterpretations } from './hooks/useInterpretations';
import { useSubtitles } from './hooks/useSubtitles';
import PreviewPage from './PreviewPage';
import TestimoniesPage from './TestimoniesPage';
const jwtDecode = require('jwt-decode');

function App() {
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  const [accessTokenExpiry, setAccessTokenExpiry] = useState(localStorage.getItem('accessTokenExpiry'));
  const savedUser = localStorage.getItem('user');
  const [user, setUser] = useState(savedUser ? JSON.parse(savedUser) : null);
  const [authenticated, setAuthenticated] = useState(user !== null);
  const [loginError, setLoginError] = useState(null);
  const [loadingServiceData, setLoadingServiceData] = useState(false);
  const [loadedServiceData, setLoadedServiceData] = useState(false);
  const [serviceData, setServiceData] = useState([]);
  const [haveServiceDataError, setHaveServiceDataError] = useState(false);
  const {getServices} = useServices();
  const {authenticate} = useAuthentication();
  const { getInterpretations, getVideo, interpretations, addInterpretation, removeInterpretation } = useInterpretations();
  const [loadingInterpretations, setLoadingInterpretations] = useState(false);
  const [loadedInterpretations, setLoadedInterpretations] = useState(false);
  const {getVideoSubtitles, getCurrentSubtitles} = useSubtitles();

  useEffect(() => {
    checkTokenValidity();
  }, []);

  const login = async (email, password) => {
    try {
      const result = await authenticate(email, password);
      setAuthenticated(true);
      setLoginError(null);
      setAccessToken(result.accessToken)
      setUser(result.user);
      localStorage.setItem('accessToken', result.accessToken);
      const tokenDecoded = jwtDecode(result.accessToken);
      localStorage.setItem('accessTokenExpiry', tokenDecoded.exp);
      localStorage.setItem('user', JSON.stringify(result.user))
      getServiceData();
    }
    catch(error) {
      setLoginError(error.message);
    }
  }

  const logout = (event) => {
    if(event) {
      event.preventDefault();
    }
    setAuthenticated(false);
    setAccessToken(null);
    setUser(null);
    setServiceData([]);
    localStorage.clear();
  }

  const checkTokenValidity = () => {
    if(accessTokenExpiry && accessTokenExpiry * 1000 <= Date.now()) {
      logout();
      return false;
    }
    return true;
  }

  const getServiceData = async () => {
    if(checkTokenValidity()) {
      setLoadingServiceData(true);
      try {
        const services = await getServices();
        setServiceData(services);
        setLoadedServiceData(true);
      }
      catch(error) {
        setServiceData([]);        
        setHaveServiceDataError(true);
      }
      finally {
        setLoadingServiceData(false);
      }
    }
  }

  const getInterpretationData = async() => {
    if(checkTokenValidity()) {
      setLoadingInterpretations(true);
      try {
        await getInterpretations(accessToken);
        setLoadedInterpretations(true);
      }
      finally {
        setLoadingInterpretations(false);
      }
    }
  }

  const renderServicesPage = useCallback(() => {
    return <ServicesPage loadingServiceData={loadingServiceData} loadedServiceData={loadedServiceData} serviceData={serviceData} getServiceData={getServiceData} />;
  }, [loadingServiceData, loadedServiceData, serviceData]);

  const renderLoginPage = useCallback(() => {
    return <LoginPage loginError={loginError} login={login} user={user} />;
  }, [loginError, user]);

  const renderResourcesPage = useCallback(() => {
    return <ResourcesPage />
  }, []);

  const renderServiceDetailsPage = useCallback(() => {
    return <ServiceDetailsPage loadingServiceData={loadingServiceData} loadedServiceData={loadedServiceData} serviceData={serviceData} getServiceData={getServiceData} getInterpretations={getInterpretationData} interpretations={interpretations} loadingInterpretations={loadingInterpretations} loadedInterpretations={loadedInterpretations} user={user} />
  }, [loadingServiceData, loadedServiceData, getServiceData, serviceData, interpretations, loadingInterpretations, loadedInterpretations, user]);

  const renderPrayerRequestsPage = useCallback(() => {
    return <PrayerRequestsPage user={user} accessToken={accessToken} />
  }, [user, accessToken]);

  const renderTestimoniesPage = useCallback(() => {
    return <TestimoniesPage user={user} accessToken={accessToken} />
  }, [user, accessToken]);


  const renderInterpretationPage = useCallback(() => {
    return <InterpretationPage 
      accessToken={accessToken} 
      addInterpretation={addInterpretation} 
      removeInterpretation={removeInterpretation} 
      loadingInterpretations={loadingInterpretations}
      loadedInterpretations={loadedInterpretations}
      interpretations={interpretations}
      getInterpretations={getInterpretationData}
      getVideo={getVideo} />
  }, [accessToken, interpretations, loadingInterpretations, loadedInterpretations]);

  const renderPreviewPage = useCallback(() => {
    return <PreviewPage key="1" user={user} accessToken={accessToken} getVideoSubtitles={getVideoSubtitles} getCurrentSubtitles={getCurrentSubtitles}  />
  }, [user, accessToken, getVideoSubtitles, getCurrentSubtitles]);


  return (
    <div className="App">    
      <BrowserRouter>
        <Header user={user} logout={logout} />
        <Switch>
          <Route exact path="/Login" component={renderLoginPage} />
          <PrivateRoute exact path="/" authenticated={authenticated} component={renderServicesPage} />
          <PrivateRoute exact path="/Resources" authenticated={authenticated} component={renderResourcesPage} />
          <PrivateRoute exact path="/Services/:id" authenticated={authenticated} component={renderServiceDetailsPage} />
          <PrivateRoute exact path="/PrayerRequests" authenticated={authenticated} component={renderPrayerRequestsPage} />
          <PrivateRoute exact path="/Testimonies" authenticated={authenticated} component={renderTestimoniesPage} />
          <PrivateRoute exact path="/Interpretation" authenticated={authenticated} component={renderInterpretationPage} />
          <PrivateRoute exact path="/Preview" authenticated={authenticated} component={renderPreviewPage} />
        </Switch>
      </BrowserRouter>
    </div>
  );
}

export default App;
